import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  addForecastBatch,
  fetchForecastBatches,
  fetchForecastFacts,
  fetchForecastPayload,
  saveForecastFacts,
  saveForecastSheets,
} from './bieForecastDataMartApi';
import {
  determinantWorkbookToFacts,
  readWorkbookFile,
  workbookToRawSheets,
} from './determinantWorkbookDataMartParser';
import {
  generateForecastModel,
  fetchForecastModelOutputs,
  fetchForecastModelSummary,
  fetchForecastModelPayload,
  commitForecastModelToSimulator,
  fetchForecastModelCommitStatus,
} from './bieForecastModelApi';

import ForecastFilterBar from './ForecastFilterBar';
import { fetchCombo } from '../core/coreapi';
import { ComboRow } from '../core/coremodels';

import ForecastBatchGrid from './ForecastBatchGrid';
import ForecastSheetGrid from './ForecastSheetGrid';
import ForecastFactGrid from './ForecastFactGrid';
import ForecastMetricGrid from './ForecastMetricGrid';
import ForecastModelGrid from './ForecastModelGrid';
import ForecastReconciliationGrid from './ForecastReconciliationGrid';

const money = (v: any) => Number(v || 0).toLocaleString();
const DEFAULT_BATCH = 'BIEFC_2027_DETERMINANT_BASE';

type State = {
  scenarios: ComboRow[];
  fiscalyears: ComboRow[];
  forecasttypes: ComboRow[];
  forecastType: string;
  fiscalYear: string;
  scenarioCode: string;
  sourceYear: string;
  targetYear: string;
  cutoffMonth: number;
  growthRate: number;
  calculationMode: string;
  file: any | null;
  filename: string;
  batches: any[];
  selected: any | null;
  facts: any[];
  sheets: any[];
  metrics: any[];
  oldPayload: any[];
  modelSummary: any[];
  modelOutputs: any[];
  modelPayload: any[];
  commitStatus: any[];
  loading: boolean;
  error: string;
  success: string;
};

class BieForecastDataMartWorkbench extends React.Component<any, State> {
  state: State = {
    scenarios: [],
    fiscalyears: [],
    forecasttypes: [],
    forecastType: '',
    fiscalYear: '2027',
    scenarioCode: 'DET2027_BASE',
    sourceYear: '2026',
    targetYear: '2027',
    cutoffMonth: 6,
    growthRate: 0,
    calculationMode: 'RECONCILIATION',
    file: null,
    filename: '',
    batches: [],
    selected: null,
    facts: [],
    metrics: [],
    sheets: [],
    oldPayload: [],
    modelSummary: [],
    modelOutputs: [],
    modelPayload: [],
    commitStatus: [],
    loading: false,
    error: '',
    success: '',
  };

  async componentDidMount() {
    await this.loadCombos();
    await this.loadBatches();
  }

  loadCombos = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      const [scenarios,fiscalyears] = await Promise.all([
        fetchCombo('biescenario'),
        fetchCombo('biefiscalyear')
      ]);
      this.setState({ scenarios });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  filters = () => {
    const {scenarios} = this.state;

    return {
      scc: scenarios || null,
    };
  };

  load = async () => {
    this.setState({ loading: true, error: '', success: '' });

    try {
      const { scc: scenario } = this.filters();
      if (!scenario) return;
      // const [dashboards, simulators] = await Promise.all([
      //   fetchDashboardSummary(scenario),
      //   fetchSimulator(scenario).catch(() => []),
      // ]);

      // this.setState({
      //   dashboard: dashboards as IBieDashboardPayload,
      //   simulator: simulators as IBieSimulatorPayload,
      // });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  batchCode = () => this.state.selected?.shc || DEFAULT_BATCH;

  setText =
    (
      name:
        | 'forecastType'
        | 'fiscalYear'
        | 'scenarioCode'
        | 'sourceYear'
        | 'targetYear'
        | 'calculationMode'
    ) =>
    (e: any) =>
      this.setState({ [name]: String(e.target.value).toUpperCase() } as any);

  setNumber = (name: 'cutoffMonth' | 'growthRate') => (e: any) =>
    this.setState({ [name]: Number(e.target.value || 0) } as any);

  onFile = (e: any) => {
    const file = e.target.files?.[0] || null;
    this.setState({ file, filename: file?.name || '' });
  };

  loadModelData = async () => {
    const { sourceYear, targetYear, scenarioCode } = this.state;
    const bac = this.batchCode();
    const [modelSummary, modelOutputs, payloadJson, commitStatus] =
      await Promise.all([
        fetchForecastModelSummary(bac, sourceYear, targetYear),
        fetchForecastModelOutputs(bac, sourceYear, targetYear),
        fetchForecastModelPayload(bac, scenarioCode, sourceYear, targetYear),
        fetchForecastModelCommitStatus(scenarioCode, bac),
      ]);
    this.setState({
      modelSummary,
      modelOutputs,
      modelPayload: payloadJson?.sd || payloadJson?.result?.sd || [],
      commitStatus,
    });
  };

  loadBatchData = async (b: any) => {
    const [facts, oldPayloadJson] = await Promise.all([
      fetchForecastFacts(b.rid, ''),
      fetchForecastPayload(b.rid, this.state.scenarioCode),
    ]);
    this.setState({
      selected: b,
      facts,
      oldPayload: oldPayloadJson?.sd || oldPayloadJson?.result?.sd || [],
    });
  };

  loadBatches = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      const batches = await fetchForecastBatches(this.state.fiscalYear, '');
      this.setState({ batches });
      if (batches.length) {
        await this.loadBatchData(batches[0]);
        await this.loadModelData();
      } else
        this.setState({
          selected: null,
          facts: [],
          oldPayload: [],
          modelSummary: [],
          modelOutputs: [],
          modelPayload: [],
          commitStatus: [],
          success: 'No forecast batches found for selected fiscal year.',
        });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  selectBatch = async (b: any) => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      await this.loadBatchData(b);
      await this.loadModelData();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  importWorkbook = async () => {
    const { file, fiscalYear, scenarioCode } = this.state;
    if (!file) {
      this.setState({ error: 'Please select determinant budget workbook.' });
      return;
    }
    this.setState({ loading: true, error: '', success: '' });
    try {
      const wb = await readWorkbookFile(file);
      const rawSheets = workbookToRawSheets(wb);
      const facts = determinantWorkbookToFacts(wb, fiscalYear);
      const batch = await addForecastBatch(
        fiscalYear,
        scenarioCode,
        file.name,
        { sheetNames: wb.SheetNames, importedAt: new Date().toISOString() }
      );
      await saveForecastSheets(batch.rid, rawSheets);
      await saveForecastFacts(batch.rid, facts);
      this.setState({ success: `Workbook imported: ${facts.length} facts.` });
      await this.loadBatches();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  generateForecast = async () => {
    const { sourceYear, targetYear, cutoffMonth, growthRate, calculationMode } =
      this.state;
    this.setState({ loading: true, error: '', success: '' });
    try {
      const res = await generateForecastModel(
        this.batchCode(),
        sourceYear,
        targetYear,
        cutoffMonth,
        growthRate,
        calculationMode
      );
      await this.loadModelData();
      this.setState({
        success: `Forecast generated. Model rows: ${res?.rc || res?.result?.rc || ''}`,
      });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  reviewReconciliation = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      await this.loadModelData();
      this.setState({ success: 'Reconciliation refreshed.' });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  commitToSimulator = async () => {
    const { scenarioCode, sourceYear, targetYear } = this.state;
    this.setState({ loading: true, error: '', success: '' });
    try {
      const res = await commitForecastModelToSimulator(
        this.batchCode(),
        scenarioCode,
        sourceYear,
        targetYear,
        true
      );
      await this.loadModelData();
      this.setState({
        success: `Committed to simulator. Payload rows: ${res?.payload_rows || res?.result?.payload_rows || ''}. Import ID: ${res?.importid || res?.result?.importid || ''}`,
      });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  openSimulator = () =>
    window.open(
      `/biesimulator?scc=${encodeURIComponent(this.state.scenarioCode)}`,
      '_blank'
    );

  renderCards() {
    const total = (arr: any[], field: string) =>
      arr.reduce((s, x) => s + Number(x[field] || 0), 0);
    const cards = [
      ['Batch', this.batchCode()],
      ['Facts', this.state.facts.length],
      ['Model Rows', this.state.modelOutputs.length],
      ['Model Payload Rows', this.state.modelPayload.length],
      ['Commit Status Rows', this.state.commitStatus.length],
      ['Actual YTD Amount', money(total(this.state.modelOutputs, 'aya'))],
      [
        'Forecast Remainder Amount',
        money(total(this.state.modelOutputs, 'fra')),
      ],
      [
        'Projected Full Year Amount',
        money(total(this.state.modelOutputs, 'pfa')),
      ],
      ['Next Year Amount', money(total(this.state.modelOutputs, 'nya'))],
    ];
    return (
      <Grid container spacing={2}>
        {cards.map(([l, v]) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={String(l)}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  {l}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {v}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  renderEngineControls() {
    const {
      loading,
      sourceYear,
      targetYear,
      cutoffMonth,
      growthRate,
      calculationMode,
    } = this.state;
    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Forecast Engine Controls</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                label="Source Year"
                value={sourceYear}
                onChange={this.setText('sourceYear')}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                label="Target Year"
                value={targetYear}
                onChange={this.setText('targetYear')}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                label="Cutoff Month"
                type="number"
                value={cutoffMonth}
                onChange={this.setNumber('cutoffMonth')}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                label="Growth Rate"
                type="number"
                value={growthRate}
                onChange={this.setNumber('growthRate')}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                label="Mode"
                value={calculationMode}
                onChange={this.setText('calculationMode')}
                fullWidth
              >
                <MenuItem value="RECONCILIATION">RECONCILIATION</MenuItem>
                <MenuItem value="OPTIMIZED">OPTIMIZED</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <Button
              variant="contained"
              onClick={this.generateForecast}
              disabled={loading}
            >
              Generate Forecast
            </Button>
            <Button
              variant="outlined"
              onClick={this.reviewReconciliation}
              disabled={loading}
            >
              Review Reconciliation
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={this.commitToSimulator}
              disabled={loading}
            >
              Commit to Simulator
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={this.openSimulator}
            >
              Open Simulator
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  renderBatchTable() {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Forecast Batches
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Batch</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>File</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {this.state.batches.map((b) => (
              <TableRow
                key={b.rid}
                hover
                sx={{
                  cursor: 'pointer',
                  bgcolor:
                    this.state.selected?.rid === b.rid
                      ? 'action.selected'
                      : undefined,
                }}
                onClick={() => this.selectBatch(b)}
              >
                <TableCell>{b.shc}</TableCell>
                <TableCell>{b.ist}</TableCell>
                <TableCell>{b.fnm}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  renderSummaryTable() {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Reconciliation Summary
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Dataset</TableCell>
              <TableCell align="right">Records</TableCell>
              <TableCell align="right">Actual YTD Qty</TableCell>
              <TableCell align="right">Forecast Rem. Qty</TableCell>
              <TableCell align="right">Projected Qty</TableCell>
              <TableCell align="right">Next Year Qty</TableCell>
              <TableCell align="right">Actual YTD Amount</TableCell>
              <TableCell align="right">Forecast Rem. Amount</TableCell>
              <TableCell align="right">Projected Amount</TableCell>
              <TableCell align="right">Next Year Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {this.state.modelSummary.map((r: any) => (
              <TableRow key={r.dsc}>
                <TableCell>{r.dsc}</TableCell>
                <TableCell align="right">{money(r.records)}</TableCell>
                <TableCell align="right">
                  {money(r.actual_ytd_quantity)}
                </TableCell>
                <TableCell align="right">
                  {money(r.forecast_remainder_quantity)}
                </TableCell>
                <TableCell align="right">
                  {money(r.projected_full_year_quantity)}
                </TableCell>
                <TableCell align="right">
                  {money(r.next_year_quantity)}
                </TableCell>
                <TableCell align="right">
                  {money(r.actual_ytd_amount)}
                </TableCell>
                <TableCell align="right">
                  {money(r.forecast_remainder_amount)}
                </TableCell>
                <TableCell align="right">
                  {money(r.projected_full_year_amount)}
                </TableCell>
                <TableCell align="right">{money(r.next_year_amount)}</TableCell>
              </TableRow>
            ))}
            {this.state.modelSummary.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>
                  No model summary yet. Click Generate Forecast.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  renderModelTable() {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Forecast Model Output
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Dataset</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Location</TableCell>
              <TableCell align="right">Actual YTD</TableCell>
              <TableCell align="right">Forecast Remainder</TableCell>
              <TableCell align="right">Projected Full Year</TableCell>
              <TableCell align="right">Next Year</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {this.state.modelOutputs.slice(0, 100).map((r: any) => (
              <TableRow key={r.rid}>
                <TableCell>{r.dsc}</TableCell>
                <TableCell>{r.prc}</TableCell>
                <TableCell>{r.lcc}</TableCell>
                <TableCell align="right">
                  {money(String(r.dsc).includes('VALUE') ? r.aya : r.ayq)}
                </TableCell>
                <TableCell align="right">
                  {money(String(r.dsc).includes('VALUE') ? r.fra : r.frq)}
                </TableCell>
                <TableCell align="right">
                  {money(String(r.dsc).includes('VALUE') ? r.pfa : r.pfq)}
                </TableCell>
                <TableCell align="right">
                  {money(String(r.dsc).includes('VALUE') ? r.nya : r.nyq)}
                </TableCell>
              </TableRow>
            ))}
            {this.state.modelOutputs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>No forecast model output yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  renderCommitTable() {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Simulator Commit Status
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Location</TableCell>
              <TableCell align="right">Records</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {this.state.commitStatus.slice(0, 100).map((r: any, i: number) => (
              <TableRow key={i}>
                <TableCell>{r.sourcecode}</TableCell>
                <TableCell>{r.sourcebatchcode}</TableCell>
                <TableCell>{r.drivercode}</TableCell>
                <TableCell>{r.entitycode}</TableCell>
                <TableCell>{r.locationcode}</TableCell>
                <TableCell align="right">{money(r.records)}</TableCell>
                <TableCell align="right">{money(r.quantity)}</TableCell>
                <TableCell align="right">{money(r.amount)}</TableCell>
              </TableRow>
            ))}
            {this.state.commitStatus.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  No simulator commit status yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  renderFactsTable() {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Loaded Forecast Dataset
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Dataset</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Location</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {this.state.facts.slice(0, 100).map((r: any) => (
              <TableRow key={r.rid}>
                <TableCell>{r.dsc}</TableCell>
                <TableCell>{r.pdc}</TableCell>
                <TableCell>{r.prc}</TableCell>
                <TableCell>{r.lcc}</TableCell>
                <TableCell align="right">{money(r.qty)}</TableCell>
                <TableCell align="right">{money(r.amt)}</TableCell>
                <TableCell>
                  {r.ssh} {r.srf}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  render() {
    const { filename, loading, error, success } = this.state;
    const { fiscalYear, scenarioCode, forecastType } = this.state;
    const { fiscalyears, scenarios, forecasttypes } = this.state;

    const { selected } = this.state;
    const { batches, facts, oldPayload, modelSummary } = this.state;
    const { modelOutputs, modelPayload, commitStatus } = this.state;
    //const { } = this.state;

    // batches: any[];
    // selected: any | null;
    // facts: any[];
    // oldPayload: any[];
    // modelSummary: any[];
    // modelOutputs: any[];
    // modelPayload: any[];
    // commitStatus: any[];

    return (
      <Box sx={{ p: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              BIE Forecast Data Mart
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review historical data, generate the YTD + Forecast model, and
              commit determinant forecast to simulator.
            </Typography>
          </Box>
          {/* <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={this.loadBatches}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              onClick={this.importWorkbook}
              disabled={loading}
            >
              Import Workbook
            </Button>
          </Stack> */}
        </Stack>

        <ForecastFilterBar
          scenarioCode={scenarioCode}
          fiscalYear={fiscalYear}
          forecastType = {forecastType}
          scenarios={ scenarios }
          forecastTypes = { forecasttypes }
          filename={filename}
          loading = { loading}
          onScenarioChange={this.setText('scenarioCode')}
          onFiscalYearChange={this.setText('fiscalYear')}
          onForecastTypeChange={this.setText('forecastType') }
          onRefresh={this.loadBatches }
          onImport = {this.importWorkbook}
          onCommit={this.commitToSimulator}
          //onFile={this.onFile}
        />

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            {/* <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                label="Fiscal Year"
                value={this.state.fiscalYear}
                onChange={this.setText('fiscalYear')}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Scenario Code"
                value={this.state.scenarioCode}
                onChange={this.setText('scenarioCode')}
                fullWidth
              />
            </Grid> */}
            {/* <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                label="Selected Workbook"
                value={this.state.filename}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ height: '100%' }}
              >
                Select File
                <input
                  hidden
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={this.onFile}
                />
              </Button>
            </Grid> */}
          </Grid>
        </Paper>

        {this.renderEngineControls()}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Stack spacing={3}>
          {/* {this.renderCards()}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>{this.renderBatchTable()}</Grid>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>{this.renderSummaryTable()}</Grid>
          {this.renderModelTable()}
          {this.renderCommitTable()}
          {this.renderFactsTable()} */}
          {this.renderCards()}
          {/* <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }}><ForecastBatchCards cards={this.state.batchCards} /></Grid>
            <Grid size={{ xs: 12, lg: 6 }}><ReconciliationSummaryCards cards={this.state.reconciliationCards} /></Grid>
          </Grid> */}

          <ForecastFactGrid rows={this.state.facts || []} loading={this.state.loading} />
          {this.renderCommitTable()}
          {this.renderFactsTable()}
          <ForecastBatchGrid 
          rows={batches || []} 
          loading={loading} 
          selectedId={selected?.rid || selected?.batchid} 
          onSelect={(selected) => this.setState({ selected }, this.loadBatches)} 
          />
          <ForecastSheetGrid rows={this.state.sheets || []} loading={loading} />
          
          <ForecastMetricGrid rows={this.state.metrics || []} loading={loading} />
          <ForecastModelGrid rows={modelOutputs || []} loading={loading} />
          <ForecastReconciliationGrid rows={this.state.modelSummary || []} loading={loading} />
          {/* // batches: any[];
          // selected: any | null;
          // facts: any[];
          // oldPayload: any[];
          // modelSummary: any[];
          // modelOutputs: any[];
          // modelPayload: any[];
          // commitStatus: any[]; */}
          {/* <ForecastBatchGrid rows={this.state.batches || []} loading={this.state.loading} selectedId={this.state.selectedBatch?.rid || this.state.selectedBatch?.batchid} onSelect={(selectedBatch) => this.setState({ selectedBatch }, this.loadBatchDetail)} />
          <ForecastSheetGrid rows={this.state.sheets || []} loading={this.state.loading} />
          <ForecastFactGrid rows={this.state.facts || []} loading={this.state.loading} />
          <ForecastMetricGrid rows={this.state.metrics || []} loading={this.state.loading} />
          <ForecastModelGrid rows={this.state.modelRows || this.state.modelOutput || []} loading={this.state.loading} />
          <ForecastReconciliationGrid rows={this.state.reconciliationRows || this.state.reconciliation || []} loading={this.state.loading} /> */}
        </Stack>
      </Box>
    );
  }
}

export default BieForecastDataMartWorkbench;

